import { Controller } from "@hotwired/stimulus"
// The swipe deck (admin/venues/swipe): a venue's unreviewed videos as a pile
// of cards. Drag the top card past a third of its width — or press ✕ / ✓ —
// and the verdict posts at once (keep shows the video on the page, drop
// hides it); the next card comes up while the answer is in flight. Undo
// puts the last card back, on the server too.
//
// The deck never waits for the server: every verdict paints at once and the
// posts run one after another in a queue, so a quick Undo after a swipe
// lands after the swipe rather than being dropped. If a post fails, the
// page reloads from the server, the only state that counts.
//
// Only the top card carries a player: a held reel plays natively (sound
// remembered under the admin page's own key), else the provider's embed loads in
// an iframe. Embeds eat every pointer event they get, so on those cards the
// buttons are the only way to swipe — the drag is a bonus for held files.
// Arrow keys and Backspace do the same from a keyboard.
export default class extends Controller {
  static targets = ["deck", "card", "slot", "keepStamp", "dropStamp", "done", "nextLink",
                    "counter", "progress", "undoButton", "dropButton", "keepButton",
                    "muteButton", "muteOn", "muteOff"]
  static values = { total: Number, done: Number, nextUrl: String, queueUrl: String }
  static MUTE_KEY = "admin.media.muted"
  static THRESHOLD = 0.33   // of the card's width
  static FLY_MS = 280

  connect() {
    this.cards = [...this.cardTargets]
    this.index = 0
    this.history = []      // cards already swiped, for Undo
    this.queue = Promise.resolve()
    this.muted = localStorage.getItem(this.constructor.MUTE_KEY) === "1"
    this._onKey = (e) => this.keydown(e)
    document.addEventListener("keydown", this._onKey)
    this.deal()
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKey)
    this.unmount()
  }

  // --- The pile -------------------------------------------------------------

  get top() { return this.cards[this.index] }

  // Bring the current card up: stack the two behind it, mount its player.
  deal() {
    this.cards.forEach((card, i) => {
      const depth = i - this.index
      card.classList.toggle("invisible", depth < 0 || depth > 1)
      if (depth < 0) return
      const d = Math.min(depth, 2)
      card.style.transition = "transform 200ms ease"
      card.style.transform = `scale(${1 - 0.04 * d}) translateY(${8 * d}px)`
      card.style.zIndex = 100 - depth
      card.style.opacity = ""
    })
    this.paintButtons()
    if (!this.top) return this.finish()
    this.doneTarget.classList.add("hidden")
    this.mount(this.top)
    this.bindDrag(this.top)
  }

  paintButtons() {
    const none = !this.top
    this.dropButtonTarget.disabled = none
    this.keepButtonTarget.disabled = none
    this.undoButtonTarget.disabled = this.history.length === 0
    this.muteButtonTarget.disabled = none || !this.top.dataset.mediaUrl
    this.muteOnTarget.classList.toggle("hidden", this.muted)
    this.muteOffTarget.classList.toggle("hidden", !this.muted)
    this.counterTarget.textContent = this.doneValue
    this.progressTarget.style.width = this.totalValue ? `${(this.doneValue * 100) / this.totalValue}%` : "0%"
  }

  finish() {
    this.unmount()
    this.doneTarget.classList.remove("hidden")
    this.nextLinkTarget.href = this.nextUrlValue || this.queueUrlValue
    this.nextLinkTarget.textContent = this.nextUrlValue ? "Next venue" : "Back to the queue"
  }

  // --- The player -----------------------------------------------------------

  mount(card) {
    this.unmount()
    const slot = card.querySelector("[data-video-swipe-target=slot]")
    const { mediaUrl, embedUrl } = card.dataset
    if (mediaUrl) {
      const v = document.createElement("video")
      v.src = mediaUrl; v.playsInline = true; v.loop = true
      v.muted = this.muted
      v.className = "w-full h-full object-cover"
      v.addEventListener("click", () => this.toggleMute())
      slot.replaceChildren(v)
      v.play().catch(() => {})
      this.player = v
    } else if (embedUrl) {
      const f = document.createElement("iframe")
      f.src = embedUrl; f.allow = "autoplay; encrypted-media"; f.setAttribute("allowfullscreen", "")
      f.className = "w-full h-full border-0 bg-black"
      slot.replaceChildren(f)
      this.player = f
    }
    this.mountedCard = card
  }

  unmount() {
    if (this.player?.tagName === "VIDEO") { this.player.pause(); this.player.removeAttribute("src") }
    this.player?.remove()
    this.player = null
    this.mountedCard = null
  }

  toggleMute() {
    this.muted = !this.muted
    localStorage.setItem(this.constructor.MUTE_KEY, this.muted ? "1" : "0")
    if (this.player?.tagName === "VIDEO") this.player.muted = this.muted
    this.paintButtons()
  }

  // --- Verdicts -------------------------------------------------------------

  keep() { this.swipe("keep") }
  drop() { this.swipe("drop") }

  swipe(verdict, { fromX = 0 } = {}) {
    const card = this.top
    if (!card) return
    this.unmount()
    this.flyAway(card, verdict === "keep" ? 1 : -1, fromX)
    this.history.push({ card, verdict })
    this.index += 1
    this.doneValue += 1
    this.deal()
    this.enqueue(card.dataset.reviewUrl, verdict, (body) => {
      if (body.remaining === 0) { this.nextUrlValue = body.nextUrl || ""; if (!this.top) this.finish() }
    })
  }

  undo() {
    const last = this.history.pop()
    if (!last) return
    this.unmount()
    this.index -= 1
    this.doneValue -= 1
    this.nextUrlValue = ""
    this.deal()
    this.enqueue(last.card.dataset.reviewUrl, "undo")
  }

  // Posts run in order; the UI has already moved on. A failure reloads:
  // whatever the server holds is the truth, the deck repaints from it.
  enqueue(url, verdict, then = () => {}) {
    this.queue = this.queue
      .then(() => this.post(url, verdict))
      .then(then)
      .catch(() => {
        alert("A verdict did not save - the deck reloads from the server.")
        location.reload()
      })
  }

  flyAway(card, direction, fromX) {
    const w = this.deckTarget.clientWidth || 400
    card.style.transition = `transform ${this.constructor.FLY_MS}ms ease-in, opacity ${this.constructor.FLY_MS}ms ease-in`
    card.style.transform = `translateX(${direction * (w + Math.abs(fromX)) * 1.2}px) rotate(${direction * 18}deg)`
    card.style.opacity = "0"
    card.style.zIndex = 200
    setTimeout(() => { card.classList.add("invisible"); card.style.transition = "" }, this.constructor.FLY_MS)
  }

  async post(url, verdict) {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content,
        "Accept": "application/json", "Content-Type": "application/json"
      },
      body: JSON.stringify({ verdict })
    })
    if (!res.ok) throw new Error(res.status)
    const body = await res.json()
    return { remaining: body.remaining, nextUrl: body.next_url }
  }

  // --- Drag -----------------------------------------------------------------

  bindDrag(card) {
    if (card.dataset.dragBound) return
    card.dataset.dragBound = "1"
    card.addEventListener("pointerdown", (e) => this.dragStart(e, card))
  }

  dragStart(e, card) {
    if (card !== this.top || e.button > 0) return
    this.drag = { card, startX: e.clientX, startY: e.clientY, dx: 0, moved: false }
    card.setPointerCapture?.(e.pointerId)
    card.style.transition = "none"
    const move = (ev) => this.dragMove(ev)
    const up = (ev) => { card.removeEventListener("pointermove", move); card.removeEventListener("pointerup", up); card.removeEventListener("pointercancel", up); this.dragEnd(ev) }
    card.addEventListener("pointermove", move)
    card.addEventListener("pointerup", up)
    card.addEventListener("pointercancel", up)
  }

  dragMove(e) {
    const d = this.drag
    if (!d) return
    d.dx = e.clientX - d.startX
    const dy = e.clientY - d.startY
    if (Math.abs(d.dx) > 6) d.moved = true
    const w = d.card.clientWidth || 400
    const lean = Math.max(-1, Math.min(1, d.dx / (w * this.constructor.THRESHOLD)))
    d.card.style.transform = `translate(${d.dx}px, ${dy * 0.3}px) rotate(${lean * 10}deg)`
    d.card.querySelector("[data-video-swipe-target=keepStamp]").style.opacity = Math.max(0, lean)
    d.card.querySelector("[data-video-swipe-target=dropStamp]").style.opacity = Math.max(0, -lean)
  }

  dragEnd() {
    const d = this.drag
    this.drag = null
    if (!d) return
    const w = d.card.clientWidth || 400
    d.card.querySelector("[data-video-swipe-target=keepStamp]").style.opacity = 0
    d.card.querySelector("[data-video-swipe-target=dropStamp]").style.opacity = 0
    if (Math.abs(d.dx) >= w * this.constructor.THRESHOLD) {
      this.swipe(d.dx > 0 ? "keep" : "drop", { fromX: d.dx })
    } else {
      d.card.style.transition = "transform 200ms ease"
      d.card.style.transform = "scale(1) translateY(0)"
      // A tap without a drag on a held reel: the video's own click toggles sound.
    }
  }

  keydown(e) {
    if (e.target.closest("input, textarea")) return
    if (e.key === "ArrowRight") { e.preventDefault(); this.keep() }
    else if (e.key === "ArrowLeft") { e.preventDefault(); this.drop() }
    else if (e.key === "Backspace") { e.preventDefault(); this.undo() }
    else if (e.key === "m") this.toggleMute()
  }
}
